import { Injectable } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { AssetClass, BlotterType } from "shared/cubiq-model-multi-asset";
import { IndexType } from "./IndexType";
import { BfxColDef, TableDefinition } from "./TableDefinition";

// real service imports
import { SysFeatures, SystemConfigPromiseService } from "shared/auth-services";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";

// Adapters
import { ColumnFilterType } from "./ColumnFilterType";

import { CurrencyPairCalendarLookupService } from "../lookup/model/impl/CurrencyPairCalendarLookupService";
import { ProfileSettingKeys } from "./core/ProfileSettingKeys";

import { CellClassParams, ValueFormatterParams } from "ag-grid-community";
import {
  BaseBMarkRiskTableDefinitionFactory,
  COLUMN_BMARK_INSTRUMENT,
} from "shared/cubiq-services/model/BaseBmarkRiskTableDefinitionFactory";
import { ValueDateHelper } from "shared/cubiq-services/model/orders/ValueDateHelper";
import { TraderGroupPromiseLookupService } from "../lookup/model/impl/TraderGroupPromiseLookupService";
import { InstrumentColumnData } from "./benchmarks/BmarkSpotRisk.constants";
import { BmarkFwdRiskDisplayModel } from "./benchmarks/BmarkSpotRiskDisplay.model";
import { FWD_RISK_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";

const logger = LoggerService.for(__dirname);
export const FWD_RISK_BLOTTER_TYPE = "bmarkFwdRisk";

/**
 * Blotter definition for Benchmark Fwd risk - Similar to spot risk but without any external group columns,
 * and a new Value Date column. Sorting is also disabled except for on instrument
 */
@Injectable()
export class BmarkFwdRiskTableDefinitionFactory extends BaseBMarkRiskTableDefinitionFactory {
  protected myValueDateHelper: ValueDateHelper;

  constructor(
    protected _sysConfigService: SystemConfigPromiseService,
    profileService: ProfilePromiseActionService,
    traderGroupLookup: TraderGroupLookupService,
    traderGroupPrimaryLookup: TraderGroupPromiseLookupService,
    currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
  ) {
    super(profileService, traderGroupLookup, traderGroupPrimaryLookup);
    this.myValueDateHelper = new ValueDateHelper(currencyPairCalendarLookupService);
  }

  protected isViewTradingGroup(tradingGroup: CubiqTS.TradingGroupImpl): boolean {
    return (
      tradingGroup.isBenchmarkFg &&
      !tradingGroup.isAutoFilledFg &&
      !tradingGroup.isExternallyFilledFg
    );
  }

  // this override for system configs applicable for blotter definition
  protected override getTableCongiPromises(): Array<Promise<any>> {
    return [this._sysConfigService.getSystemConfig(SysFeatures.benchmarksEnabled)];
  }
  // this override to set flags returned by promises
  protected override handleTableCongiPromises(values: Array<any>) {
    this.myBmarkEnabled = this.valueOn(values[0]);
  }

  public createDef(): TableDefinition {
    super.createDef();
    logger.out.info(`${this.constructor.name}.createDef()`);

    let position = this.position;
    let td = new TableDefinition("id", COLUMN_BMARK_INSTRUMENT);

    td.benchmarkTableDefinition = true;

    /****************************************************/
    /* INSTRUMENT (Currency pair) */
    const instrument: BfxColDef = this.createInstrumentCol();

    // Remove alert and warnings from instrument col - for fwds, these are on the totals
    delete instrument.cellClassRules;
    instrument.cellClass = ["blotter_fwd-risk_instrument"];

    // Add custom comparator so value dates are alwyas ascending regardless of invertSort flag
    instrument.comparator = (
      instrument1: InstrumentColumnData,
      instrument2: InstrumentColumnData,
      nodeA,
      nodeB,
      isInverted,
    ) => {
      const order1: BmarkFwdRiskDisplayModel = nodeA.data as BmarkFwdRiskDisplayModel;
      const order2: BmarkFwdRiskDisplayModel = nodeB.data as BmarkFwdRiskDisplayModel;
      const pair1 = instrument1?.instrument;
      const pair2 = instrument2?.instrument;

      if (pair1 == null && pair2 == null) {
        return 0;
      }
      if (pair1 == null) {
        return 1;
      }
      if (pair2 == null) {
        return -1;
      }
      if (pair1 != pair2) {
        return pair1.localeCompare(pair2);
      }

      const ten1 = order1.getTenor();
      const ten2 = order2.getTenor();

      // We don't want value dates to be reverse sorted
      const ltValue = isInverted ? 1 : -1;
      const gtValue = isInverted ? -1 : 1;

      if (ten1 === CubiqTS.Tenor.SPOT) {
        return ten2 == null || ten2 > CubiqTS.Tenor.SPOT ? ltValue : gtValue;
      }
      if (ten2 === CubiqTS.Tenor.SPOT) {
        return ten1 == null || ten1 > CubiqTS.Tenor.SPOT ? gtValue : ltValue;
      }

      const mom1 = order1.getValueDate() as CubiqTS.Moment;
      const mom2 = order2.getValueDate() as CubiqTS.Moment;
      if (mom1 == null && mom2 == null) {
        return 0;
      }
      if (mom1 == null) {
        return gtValue;
      }
      if (mom2 == null) {
        return ltValue;
      }

      let result = mom1.isSame(mom2) ? 0 : mom1.isAfter(mom2) ? gtValue : ltValue;
      return result;
    };
    // Remove 'none' option from sorting order so will alwys be ordered by instrument
    instrument.sortingOrder = ["asc", "desc"];

    td.addColumn(instrument);

    /****************************************************/
    // Total
    let total: BfxColDef = this.createTotalCol();
    total.headerName = "Total (Internal)";
    total.disableInPicker = true;
    total.sortable = false;
    total.cellClass = ["blotter_fwd-risk_total"];
    td.addColumn(total);

    let vd: BfxColDef = this.initColdDef();
    vd.headerName = "Value Date";
    vd.disableInPicker = true;
    vd.hide = false;
    vd.visible = true;
    vd.field = "valueDate";
    vd.minWidth = this.oneWs;
    vd.width = this.eightWs;
    vd.indexType = IndexType.AgGridDefault;
    vd.columnFilterType = ColumnFilterType.DATE_TENOR;
    vd.columnFilterData = CubiqTS.Tenor;
    vd.sortable = false;

    // Note: ws can't use cellClass here as that is only called if the actual cell value changes
    vd.cellClassRules = {
      "blotter_fwd-risk_value-date": () => true,
      positionWarning: (params: CellClassParams) => params.data?.isPositionWarning,
      positionAlert: (params: CellClassParams) => params.data?.isPositionAlert,
    };

    vd.valueGetter = (params) => {
      if (params.data instanceof BmarkFwdRiskDisplayModel) {
        return params.data.getValueDate();
      }
      return "Dunno";
    };
    vd.valueFormatter = (params: ValueFormatterParams) => {
      if (params.data instanceof BmarkFwdRiskDisplayModel) {
        if (params.data.getTenor() == CubiqTS.Tenor.SPOT) {
          return "Spot";
        }

        return this.myValueDateHelper.formatValueDate(
          params.data.getValueDate(),
          params.data.getTenor(),
        );
      }
      return "Dunno";
    };
    if (!this.restoreFromConfig(vd)) {
      vd.hide = true;
      vd.position = this.position.next();
    }
    td.addColumn(vd);

    const groupCols = this.createTraderGroupCols(this.bmarkTradingGroups);
    for (const col of groupCols) {
      col.sortable = false;
      col.skipExport = true;
      td.addColumn(col);
    }

    return td;
  }

  public getBlotterType() {
    return BlotterType.FWD_RISK;
  }

  public getAssetClass() {
    return AssetClass.FIX;
  }

  /**
   * Helper functions
   */

  public getBlotterVersion(): string {
    return this.getVersionIdentifier();
  }

  public getVersionIdentifier(): string {
    return FWD_RISK_TABLE_DEFINITION_VER;
  }

  protected getProfileSettingKey(): string {
    return `${ProfileSettingKeys.userProfileDefaultBlotter}${FWD_RISK_BLOTTER_TYPE}`;
  }

  protected override determineDefaultConfig(defaultConfig: any) {
    // Need to make sure that the default sort is unchanged
    defaultConfig.sortingModel = this.defaultSortModel.concat();
    super.determineDefaultConfig(defaultConfig);
  }
}
